import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import { UserAuthContext } from '../UserAuthContext';
import { getProductImageUrl, useProductImageFallback } from '../utils/productImages';
import './Checkout.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const money = (value) => (Number(value) || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD'
});

function PaymentForm({ order, onError, navigate, paymentUnavailable }) {
  const stripe = useStripe();
  const elements = useElements();
  const [poNumber, setPoNumber] = useState(order.PO_Number || '');
  const [moreDetails, setMoreDetails] = useState(order.More_Details || '');
  const [submitting, setSubmitting] = useState(false);
  // Set once Stripe has actually captured the charge but our backend never
  // confirmed the order. The form stays locked from then on -- the card was
  // already charged, so re-enabling "Pay" risks a second charge.
  const [paymentLocked, setPaymentLocked] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const submissionLock = useRef(false);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Confirms the order on our backend after Stripe has already succeeded.
  // Retried a few times with a short backoff first, since most failures at
  // this point are a transient network blip rather than a real rejection.
  const confirmOrderWithRetry = async (token, paymentIntentId) => {
    const backoffsMs = [0, 1000, 2500];
    for (let attempt = 0; attempt < backoffsMs.length; attempt += 1) {
      if (backoffsMs[attempt] > 0) await sleep(backoffsMs[attempt]);
      try {
        await axios.put(
          `${API_BASE}/api/cart/orders/${order.idorders}/checkout`,
          {
            order_state: 'Processing',
            PO_Number: poNumber || null,
            More_Details: moreDetails || null,
            stripe_payment_intent_id: paymentIntentId
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return true;
      } catch (confirmError) {
        console.error(`Order confirmation attempt ${attempt + 1} failed:`, confirmError);
      }
    }
    return false;
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    if (submissionLock.current || paymentLocked || paymentUnavailable) return;
    if (!stripe || !elements) {
      onError('Secure payment is still loading. Please wait a moment.');
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    onError('');

    try {
      const token = localStorage.getItem('user_token');
      if (!token) throw new Error('Your session expired. Please log in again.');

      const { data } = await axios.post(
        `${API_BASE}/api/cart/orders/${order.idorders}/create-payment-intent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.alreadyPaid) {
        navigate(`/thank-you?type=order&order=${encodeURIComponent(order.idorders)}`);
        return;
      }
      if (!data.clientSecret) throw new Error('The server did not create a secure payment session.');

      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: order.companyName || 'Valley customer'
          }
        }
      });

      if (result.error) throw new Error(result.error.message);
      if (result.paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment could not be confirmed.');
      }

      // Stripe has captured the charge at this point. Persist the reference
      // immediately so it is never lost, even if the confirmation call
      // below fails or the tab closes before it completes.
      const paymentIntentId = result.paymentIntent.id;
      try {
        localStorage.setItem(`valley_paid_order_${order.idorders}`, paymentIntentId);
      } catch { /* localStorage unavailable, non-fatal */ }
      setPaymentReference(paymentIntentId);

      const confirmed = await confirmOrderWithRetry(token, paymentIntentId);

      if (!confirmed) {
        // The card WAS charged -- never claim the payment failed here, and
        // never let the form be resubmitted, or the customer could be
        // charged twice. Lock the form and point them to support instead.
        setPaymentLocked(true);
        onError(
          `Your payment was processed, but we had a problem confirming your order. ` +
          `Please do not try to pay again -- contact us with this reference: ${paymentIntentId}`
        );
        return;
      }

      try { localStorage.removeItem(`valley_paid_order_${order.idorders}`); } catch { /* non-fatal */ }
      navigate(`/thank-you?type=order&order=${encodeURIComponent(order.idorders)}`);
    } catch (error) {
      console.error('Checkout failed:', error);
      onError(
        error.response?.status === 400
          ? 'The payment details were not accepted.'
          : error.response?.status === 401
            ? 'Your session expired. Please log in again.'
            : error.response?.data?.error || error.message || 'Payment could not be completed.'
      );
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <form className="co-payment-form" onSubmit={submitPayment}>
      <div className="co-field">
        <label htmlFor="checkout-po">PO number <span>Optional</span></label>
        <input
          id="checkout-po"
          type="text"
          value={poNumber}
          onChange={(event) => setPoNumber(event.target.value.slice(0, 30))}
          placeholder="Your purchase order number"
          autoComplete="off"
          disabled={paymentLocked || paymentUnavailable}
        />
      </div>

      <div className="co-field">
        <label htmlFor="checkout-card">Card information</label>
        <div className="co-card-field">
          <CardElement
            id="checkout-card"
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#071522',
                  fontFamily: 'DM Sans, sans-serif',
                  '::placeholder': { color: '#89969d' }
                },
                invalid: { color: '#aa3d28' }
              },
              disabled: paymentLocked || paymentUnavailable
            }}
          />
        </div>
      </div>

      <div className="co-field">
        <label htmlFor="checkout-notes">Payment notes <span>Optional</span></label>
        <textarea
          id="checkout-notes"
          value={moreDetails}
          onChange={(event) => setMoreDetails(event.target.value.slice(0, 255))}
          placeholder="Add a note for our order team"
          rows="4"
          disabled={paymentLocked || paymentUnavailable}
        />
      </div>

      {paymentLocked && (
        <div className="co-payment-locked-notice" role="alert" style={{ marginBottom: '1rem' }}>
          <strong>Your card has already been charged -- do not pay again.</strong>
          <p>
            We had a problem confirming your order after payment. Please contact us with this
            reference so our team can confirm it manually: <code>{paymentReference}</code>
          </p>
        </div>
      )}
      {paymentUnavailable && (
        <div className="co-pricing-notice" role="status">
          <i className="fas fa-circle-exclamation" />
          <div><strong>Payment temporarily unavailable</strong><p>Every product must have a price before this order can be paid.</p></div>
        </div>
      )}
      <button className="co-pay-button" type="submit" disabled={!stripe || submitting || paymentLocked || paymentUnavailable}>
        {paymentLocked ? 'Contact support to continue' : paymentUnavailable ? 'Waiting for product pricing' : submitting ? 'Processing secure payment…' : `Pay ${money(order.total)}`}
        <span>{submitting ? '·' : '→'}</span>
      </button>
      <button className="co-cancel-button" type="button" onClick={() => navigate('/orders')}>
        Return to orders
      </button>

      <div className="co-secure">
        <i className="fas fa-lock" />
        <p><strong>Secure card payment</strong><span>Payment information is encrypted and processed by Stripe.</span></p>
      </div>
    </form>
  );
}

export default function Checkout() {
  const { id } = useParams();
  const { user, loading: authLoading } = useContext(UserAuthContext);
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const paymentUnavailable = Boolean(order) && (
    order.payment_available === false ||
    order.items.some((item) => !Number.isFinite(item.product_price) || item.product_price <= 0)
  );

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signup', { replace: true, state: { returnTo: `/checkout/${id}` } });
    }
  }, [authLoading, user, navigate, id]);

  useEffect(() => {
    if (authLoading || !user) return;
    const controller = new AbortController();

    async function loadOrder() {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('user_token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_BASE}/api/cart/orders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        const data = response.data;
        if (data.order_state === 'Processing' && data.payment_status === 'succeeded') {
          navigate(`/thank-you?type=order&order=${encodeURIComponent(data.idorders)}`, { replace: true });
          return;
        }
        if (data.order_state !== 'Waiting for Payment') {
          throw new Error('This order is not awaiting payment.');
        }

        setOrder({
          ...data,
          subtotal: Number.parseFloat(data.subtotal) || 0,
          shipping_cost: Number.parseFloat(data.shipping_cost) || 0,
          taxes: Number.parseFloat(data.taxes) || 0,
          tariffs: Number.parseFloat(data.tariffs) || 0,
          total: Number.parseFloat(data.total) || 0,
          items: Array.isArray(data.items) ? data.items.map((item) => ({
            ...item,
            product_price: Number.parseFloat(item.product_price) || 0,
            product_image: getProductImageUrl(item.product_image)
          })) : []
        });
      } catch (requestError) {
        if (requestError.name !== 'CanceledError') {
          console.error('Unable to load checkout order:', requestError);
          setError(
            requestError.response?.status === 404
              ? 'This order could not be found.'
              : requestError.response?.status === 401
                ? 'Your session expired. Please log in again.'
                : requestError.message || 'Order details could not be loaded.'
          );
        }
      } finally {
        setLoading(false);
      }
    }

    loadOrder();
    return () => controller.abort();
  }, [user, authLoading, id, navigate]);

  return (
    <div className="co-page">
      <Header />
      <main className="co-main">
        {authLoading || loading ? (
          <div className="co-state">
            <span className="co-spinner" />
            <h1>Preparing secure checkout…</h1>
            <p>We are confirming your order details.</p>
          </div>
        ) : error || !order ? (
          <div className="co-state co-error-state">
            <span><i className="fas fa-exclamation-circle" /></span>
            <h1>Checkout unavailable</h1>
            <p>{error || 'This order is unavailable.'}</p>
            <Link to="/orders">Return to orders <b>→</b></Link>
          </div>
        ) : (
          <>
            <div className="co-hero">
              <div>
                <span className="co-eyebrow">Secure checkout</span>
                <h1>Complete order #{order.idorders}.</h1>
                <p>Review the delivery details and submit payment to move your order into processing.</p>
              </div>
              <div className="co-progress" aria-label="Checkout progress">
                <span className="is-complete"><b><i className="fas fa-check" /></b> Cart</span><i />
                <span className="is-complete"><b><i className="fas fa-check" /></b> Order</span><i />
                <span className="is-active"><b>3</b> Payment</span>
              </div>
            </div>

            {error && <div className="co-inline-error" role="alert"><i className="fas fa-exclamation-circle" /> {error}</div>}

            <div className="co-layout">
              <section className="co-order-panel">
                <div className="co-panel-heading">
                  <span>Order review</span>
                  <h2>{order.items.length} {order.items.length === 1 ? 'product' : 'products'}</h2>
                </div>

                <div className="co-items">
                  {order.items.map((item) => (
                    <article key={item.idorder_item}>
                      <div className="co-item-image">
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          onError={useProductImageFallback}
                        />
                      </div>
                      <div>
                        <span>{item.product_brand || 'Industrial automation'}</span>
                        <h3>{item.product_name}</h3>
                        <p>Quantity {item.quantity}</p>
                      </div>
                      <strong>{item.product_price > 0 ? money(item.product_price * item.quantity) : 'Price pending'}</strong>
                    </article>
                  ))}
                </div>

                <div className="co-addresses">
                  <div>
                    <span><i className="fas fa-truck" /> Shipping address</span>
                    <p>{order.shipping_address || 'No shipping address provided'}<br />{order.zipCode || ''}</p>
                  </div>
                  <div>
                    <span><i className="fas fa-file-invoice" /> Billing address</span>
                    <p>{order.billing_address || 'No billing address provided'}<br />{order.companyName || ''}</p>
                  </div>
                </div>
              </section>

              <aside className="co-payment-panel">
                <div className="co-panel-heading">
                  <span>Payment</span>
                  <h2>Order total</h2>
                </div>
                <div className="co-totals">
                  <p><span>Subtotal</span><strong>{money(order.subtotal)}</strong></p>
                  <p><span>Shipping</span><strong>{money(order.shipping_cost)}</strong></p>
                  <p><span>Taxes</span><strong>{money(order.taxes)}</strong></p>
                  {order.tariffs > 0 && <p><span>Tariffs</span><strong>{money(order.tariffs)}</strong></p>}
                  <p className="co-total"><span>{paymentUnavailable ? 'Total pending' : 'Total due'}</span><strong>{paymentUnavailable ? '—' : money(order.total)}</strong></p>
                </div>
                <Elements stripe={stripePromise}>
                  <PaymentForm order={order} onError={setError} navigate={navigate} paymentUnavailable={paymentUnavailable} />
                </Elements>
              </aside>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
